import colorParser from 'color';
import {
  convertHexaOrHexToRGBAUnits,
  isHexaColor,
  isHexColor,
  isRGBColor,
} from '@wix/editor-elements-common-utils';
import { OpenColorPickerFunction } from '@wix/editor-elements-types/src/editorExternal/store/actions';
import type { Color, ThemeColor } from '../adapters/types';
import type { SiteColorMap } from '../types';
import {
  isThemeColor,
  legacyToStructuredStyleProperties,
} from '../adapters/legacyToStructuredStyleProperties';
import type { BiConfigType } from './types';
import { TextCssVars } from './TextDesign/types';

export const isSectionVisible = (props: Record<string, any>) =>
  props.isVisible?.() ?? true;

export const stringifyColor = (color: Color | ThemeColor) => {
  if (color.type === 'ThemeColor') {
    return `rgba(var(--${color.theme}),${color.overrides?.alpha ?? 1})`;
  } else {
    return `rgba(${color.red},${color.green},${color.blue},${
      color.alpha ?? 1
    })`;
  }
};

export const rgbaToHex = (color: string) => {
  try {
    return colorParser(color).hex();
  } catch {
    return '';
  }
};

export const rgbaToOpacity = (color: string) => {
  try {
    return colorParser(color).alpha();
  } catch {
    return 1;
  }
};

export const getColorString = (color: ThemeColor | Color | string): string => {
  if (typeof color === 'string') {
    return color;
  }
  if (color.type === 'ThemeColor') {
    return color.theme;
  }
  return rgbaToHex(
    `rgba(${color.red},${color.green},${color.blue},${color.alpha})`,
  );
};

export const formatShadowColor = (colorValue: string, alpha?: number) => {
  if (isThemeColor(colorValue)) {
    return `rgba(var(--${colorValue}),${alpha})`;
  }
  if (isHexColor(colorValue)) {
    const hexAlpha = alpha
      ? Math.floor(alpha * 255)
          .toString(16)
          .toLocaleUpperCase()
      : '';
    return `${colorValue}${hexAlpha}`;
  }
  if (isRGBColor(colorValue)) {
    return `rgba(${colorValue},${alpha || 1})`;
  }
  return colorValue;
};

export const getOutlineColor = (textOutline: string) => {
  const layers = textOutline.split(',');
  const fullColorExp = layers[0].split(' ').at(-1) || '';
  const themeColor = fullColorExp.match(/color_\d+/)?.[0];
  return themeColor ?? fullColorExp;
};

export const getOutlineHexColor = (
  textOutline: string,
  siteColorMap: SiteColorMap,
) => {
  const textOutlineColor = getOutlineColor(textOutline);
  if (isHexColor(textOutlineColor) || isHexaColor(textOutlineColor)) {
    return textOutlineColor;
  }
  return siteColorMap[textOutlineColor];
};

export const getCssColor = (
  color: ThemeColor | Color | string,
  siteColorMap: SiteColorMap,
): string => {
  if (typeof color === 'object' && color.type === 'ThemeColor') {
    const siteColor = siteColorMap[color.theme];
    const [r, g, b] = convertHexaOrHexToRGBAUnits(siteColor) ?? [];
    return `rgba(${r},${g},${b},${color.overrides?.alpha ?? 1})`;
  }
  return getColorString(color);
};

export const getOpacity = (color: ThemeColor | Color | string): number => {
  if (typeof color === 'string') {
    return rgbaToOpacity(color);
  }
  if (color.type === 'ThemeColor') {
    return color.overrides?.alpha ?? 1;
  }
  return color.alpha;
};

export type OpenColorPickerType = ReturnType<typeof generateOpenColorPicker>;

export const convertStringColorToPropStructure = (
  prop: string,
  value: string,
  opacity?: number,
) => {
  const definition = { [prop]: { type: 'COLOR' } };
  const isOpacityDefined = typeof opacity !== 'undefined';
  const structuredColorValue = legacyToStructuredStyleProperties(
    {
      style: {
        properties: {
          [prop]: value,
          ...(isOpacityDefined && { [`alpha-${prop}`]: opacity }),
        },
      },
    } as any,
    definition as any,
  )[prop];
  return { [prop]: structuredColorValue };
};

export const generateOpenColorPicker = (
  openColorPicker: OpenColorPickerFunction,
) => {
  return (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>,
    value: Color | ThemeColor | string,
    onChange: (valueToUpdate: any, biConfig?: BiConfigType) => void,
    biConfig?: BiConfigType,
  ) => {
    const { clientY, clientX } = event;
    const stringValue = getColorString(value);

    const onChangeInner = (newValue: any) => onChange(newValue, biConfig);

    return openColorPicker?.(
      stringValue,
      { top: clientY, left: clientX },
      { onChange: onChangeInner },
    );
  };
};

export const shouldShowSection = (
  param: keyof TextCssVars,
  value: Record<string, any> | undefined,
) => value?.hasOwnProperty(param);

export const isEqual = (a: unknown, b: unknown): boolean => {
  if (typeof a !== typeof b) {
    return false;
  }

  // Handle null explicitly since typeof null is 'object'
  if (a === null || b === null) {
    return a === b;
  }

  if (typeof a !== 'object') {
    return a === b;
  }

  if (typeof a === 'function' || typeof a === 'symbol') {
    return a.toString() === b?.toString();
  }

  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);

  if (aProps.length !== bProps.length) {
    return false;
  }

  for (const propName of aProps) {
    if (!b?.hasOwnProperty(propName)) {
      return false;
    }

    // @ts-expect-error
    if (!isEqual(a[propName], b[propName])) {
      return false;
    }
  }

  return true;
};

export const isObject = (value: unknown) =>
  typeof value === 'object' && !(value instanceof Array);

export const getAlpha = (color: ThemeColor | Color | undefined) =>
  color?.type === 'ThemeColor' ? color.overrides?.alpha ?? 1 : color?.alpha;
